import { useMemo, useState } from 'react'
import { Button, Dialog, DialogTrigger, Popover } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { cn } from 'src/utils/cx'
import { track } from 'src/utils/tracker'
import IcBaselineAutoAwesome from '~icons/ic/baseline-auto-awesome'
import { useRecommendedTags } from '../Tag/TagRecommendation'
import { TagCategory } from '../Tag/utils'

export function TitleTagSuggestion({
  mediaId,
  isVideoTask,
  onSelectTag,
}: {
  mediaId?: string
  isVideoTask?: boolean
  onSelectTag: (name: string) => void
}) {
  const { t } = useTranslation(['artwork'])
  const [isOpen, setIsOpen] = useState(false)

  const { tags: categorizedTags, loading } = useRecommendedTags({
    mediaId,
    isVideoTask,
  })

  const filteredTags = useMemo(
    () =>
      categorizedTags
        .filter(
          group =>
            group.category === TagCategory.Category ||
            group.category === TagCategory.Feature,
        )
        .flatMap(group => group.data),
    [categorizedTags],
  )

  const hasNoTags = !loading && filteredTags.length === 0

  if (!mediaId || hasNoTags) return null

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <Button
        isDisabled={loading}
        aria-label={t('artwork:title-suggestions.heading')}
        className="flex-none inline-flex items-center justify-center size-9 rounded-lg text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors disabled:opacity-40"
      >
        <IcBaselineAutoAwesome className="size-5" />
      </Button>
      <Popover
        placement="bottom end"
        className="animate-in fade-in slide-in-from-top-1 duration-200 rounded-lg drop-shadow-lg bg-modal-background p-3 max-w-[280px]"
      >
        <Dialog className="outline-none">
          <p className="text-xs text-neutral-500 mb-2">
            {t('artwork:title-suggestions.heading')}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {filteredTags.map(tag => (
              <button
                key={tag.name}
                type="button"
                className={cn(
                  'px-2.5 py-1 text-sm rounded-full',
                  'bg-neutral-100 dark:bg-neutral-800',
                  'hover:bg-primary/10 hover:text-primary',
                  'transition-colors cursor-pointer',
                )}
                onClick={() => {
                  onSelectTag(tag.name)
                  track('publish_title_tag_selected', {
                    tagName: tag.name,
                  })
                }}
              >
                {tag.name}
              </button>
            ))}
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  )
}
